import PropTypes from 'prop-types';
import React from 'react';
import { kebabCase } from 'lodash-es';
import MenuSection from './MenuSection';
import SubMenu from './SubMenu';

export default class MegaMenu extends React.Component {
  navRef = React.createRef();

  componentDidMount() {
    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    this.smallDesktopMediaQuery = window.matchMedia(
      '(min-width: 768px and max-width: 1007px)',
    );

    if (this.mobileMediaQuery.matches) {
      this.props.toggleDisplayHidden(true);
    }

    this.mobileMediaQuery.addListener(this.resetDefaultState);
    document.body.addEventListener('click', this.handleDocumentClick, false);

    if (this.navRef.current) {
      this.navRef.current.addEventListener('focusout', this.handleFocusOut);
      this.navRef.current.addEventListener('keydown', this.handleKeyDown);
    }
  }

  /**
   * Remove event listener
   */
  componentWillUnmount() {
    this.props.updateCurrentSection('');
    this.props.toggleDisplayHidden(true);
    this.mobileMediaQuery.removeListener(this.resetDefaultState);
    document.body.removeEventListener('click', this.handleDocumentClick, false);

    if (this.navRef.current) {
      this.navRef.current.removeEventListener('focusout', this.handleFocusOut);
      this.navRef.current.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  getSubmenu(item, currentSection) {
    if (this.mobileMediaQuery?.matches) {
      const menuSections = [
        item.menuSections.mainColumn,
        item.menuSections.columnOne,
        item.menuSections.columnTwo,
      ].reduce((acc, column) => {
        acc.push({
          title: column.title,
          links: {
            columnOne: {
              title: '',
              links: column.links,
            },
            columnTwo: {
              title: '',
              links: [],
            },
          },
        });

        return acc;
      }, []);

      return menuSections.map((section, i) => (
        <MenuSection
          key={`${section}-${i}`}
          title={section.title}
          currentSection={currentSection}
          updateCurrentSection={() => this.updateCurrentSection(section.title)}
          links={section.links}
          linkClicked={this.props.linkClicked}
          mobileMediaQuery={this.mobileMediaQuery}
          smallDesktopMediaQuery={this.smallDesktopMediaQuery}
          columnThreeLinkClicked={this.props.columnThreeLinkClicked}
        />
      ));
    }

    return (
      <SubMenu
        data={item.menuSections}
        navTitle={item.title}
        handleBackToMenu={() => this.toggleDropDown('')}
        show={this.props.currentDropdown !== ''}
        linkClicked={this.props.linkClicked}
        mobileMediaQuery={this.mobileMediaQuery}
        smallDesktopMediaQuery={this.smallDesktopMediaQuery}
        columnThreeLinkClicked={this.props.columnThreeLinkClicked}
      />
    );
  }

  handleDocumentClick = event => {
    if (this.props.currentDropdown && !this.menuRef.contains(event.target)) {
      this.props.toggleDropDown('');
      this.props.updateCurrentSection('');
    }
  };

  // When keyboard focus leaves the open panel, collapse it so it does not
  // obscure the focus indicator of the element that now has focus (WCAG
  // 2.4.7). focusout bubbles, so event.relatedTarget is the element about to
  // receive focus. We keep the panel open only while focus stays within that
  // panel or returns to its trigger button; moving to a sibling top-level
  // button (or out of the nav entirely) closes it.
  handleFocusOut = event => {
    if (this.mobileMediaQuery?.matches || !this.props.currentDropdown) {
      return;
    }
    const nextFocusedElement = event.relatedTarget;
    const openPanel = this.navRef.current.querySelector(
      '.vetnav-panel:not([hidden])',
    );
    const openTrigger = this.navRef.current.querySelector(
      '[aria-expanded="true"]',
    );
    const stayingInOpenPanel =
      nextFocusedElement &&
      (openPanel?.contains(nextFocusedElement) ||
        nextFocusedElement === openTrigger);

    if (!stayingInOpenPanel) {
      this.props.toggleDropDown('');
      this.props.updateCurrentSection('');
    }
  };

  // Escape collapses the open panel and returns focus to its trigger button.
  handleKeyDown = event => {
    if (event.key !== 'Escape' || !this.props.currentDropdown) {
      return;
    }
    const activeTrigger = this.menuRef.querySelector('[aria-expanded="true"]');
    this.props.toggleDropDown('');
    this.props.updateCurrentSection('');
    if (activeTrigger) {
      activeTrigger.focus();
    }
  };

  resetDefaultState = () => {
    if (this.mobileMediaQuery?.matches) {
      this.props.toggleDisplayHidden(true);
    } else {
      this.props.toggleDisplayHidden(false);
    }
    this.props.updateCurrentSection('');
    this.props.toggleDropDown('');
  };

  toggleDropDown(title) {
    if (this.props.currentDropdown === title) {
      this.props.toggleDropDown('');
    } else {
      this.props.toggleDropDown(title);
    }
  }

  updateCurrentSection(title) {
    let sectionTitle = title;

    if (this.mobileMediaQuery?.matches) {
      sectionTitle = this.props.currentSection === title ? '' : title;
    }

    this.props.updateCurrentSection(sectionTitle);
  }

  render() {
    const {
      currentDropdown,
      currentSection,
      data,
      display,
      linkClicked,
      columnThreeLinkClicked,
    } = this.props;

    const hasOpenSubMenu = currentSection !== '';

    return (
      <div className="hidden-header login-container" {...display}>
        <div
          className="row va-flex"
          ref={el => {
            this.menuRef = el;
          }}
        >
          <nav id="vetnav" aria-label="Primary" ref={this.navRef}>
            <ul id="vetnav-menu">
              <li>
                <a
                  className="vetnav-level1"
                  data-testid="mobile-home-nav-link"
                  href="/"
                >
                  Home
                </a>
              </li>
              {data.map((item, i) => (
                <li
                  key={`${kebabCase(item.title)}-${i}`}
                  className={`${item.className || ''} ${
                    item.currentPage
                      ? 'current-page medium-screen:vads-u-margin-right--0'
                      : ''
                  }`}
                >
                  {item.menuSections ? (
                    <button
                      type="button"
                      aria-expanded={currentDropdown === item.title}
                      aria-controls={`vetnav-${kebabCase(item.title)}`}
                      aria-haspopup={!!item.menuSections}
                      className="vetnav-level1"
                      data-e2e-id={`${kebabCase(item.title)}-${i}`}
                      onClick={() => {
                        this.toggleDropDown(item.title);
                        this.props.updateCurrentSection('');
                      }}
                    >
                      {item.title}
                    </button>
                  ) : (
                    <a
                      // My VA and My HealtheVet use less side padding so the
                      // row fits from 768px in Public Sans.
                      className={`vetnav-level1 ${
                        item.compact
                          ? 'medium-screen:vads-u-padding-y--2 medium-screen:vads-u-padding-x--1p5'
                          : 'medium-screen:vads-u-padding--2'
                      }`}
                      data-e2e-id={`${kebabCase(item.title)}-${i}`}
                      href={item.href}
                      onClick={linkClicked.bind(null, item)}
                    >
                      {item.title}
                    </a>
                  )}
                  <div
                    id={`vetnav-${kebabCase(item.title)}`}
                    className={`vetnav-panel ${
                      hasOpenSubMenu ? 'vetnav-submenu--expanded' : ''
                    }`}
                    hidden={currentDropdown !== item.title}
                  >
                    {item.title === currentDropdown && item.menuSections && (
                      <ul aria-label={item.title}>
                        {Array.isArray(item.menuSections)
                          ? item.menuSections.map((section, j) => (
                              <MenuSection
                                key={`${section}-${j}`}
                                title={section.title}
                                currentSection={currentSection}
                                updateCurrentSection={() =>
                                  this.updateCurrentSection(section.title)
                                }
                                href={section.href}
                                links={section.links}
                                linkClicked={linkClicked}
                                mobileMediaQuery={this.mobileMediaQuery}
                                smallDesktopMediaQuery={
                                  this.smallDesktopMediaQuery
                                }
                                columnThreeLinkClicked={columnThreeLinkClicked}
                              />
                            ))
                          : this.getSubmenu(item, currentSection)}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    );
  }
}

MegaMenu.propTypes = {
  /**
   * This is the data that will generate the navigation
   * Data is made up an array of objects
   * Read Notes tab to see the structure of the data prop
   */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      menuSections: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    }),
  ).isRequired,
  /**
   * Function to update if the MegaMenu is displayed or not
   */
  toggleDisplayHidden: PropTypes.func.isRequired,
  /**
   * Function to update currentDropdown in state
   */
  toggleDropDown: PropTypes.func.isRequired,
  /**
   * Function to update currentSection in state
   */
  updateCurrentSection: PropTypes.func.isRequired,
  /**
   * Optional function to intercept links clicked at column three
   */
  columnThreeLinkClicked: PropTypes.func,
  /**
   * String value of current dropdown
   */
  currentDropdown: PropTypes.string,

  /**
   * String value of current dropdown section
   */
  currentSection: PropTypes.string,

  display: PropTypes.shape({
    hidden: PropTypes.bool,
  }),

  /**
   * Optional function to intercept links clicked
   */
  linkClicked: PropTypes.func,
};

MegaMenu.defaultProps = {
  currentDropdown: '',
  currentSection: '',
  display: {},
  linkClicked() {},
  columnThreeLinkClicked() {},
};
